import { initLoyaltyNotifications } from './loyalty-provider-notifications.js?v=20261009-notification-design-1';
import './loyalty-rested-status.js?v=20261005-rested-operational';
import { initLoyaltyBirthdayForm } from './loyalty-birthday-form.js?v=20261009-birthday-ui-2';

/* LoyaltyLion page presentation and accessibility helpers.
 * The Rested card is informational only. The signed proxy and Warehouse remain
 * authoritative for current availability and exact-variant awards.
 */

export function formatHistoryDate(value) {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value.trim());
  if (!match) return null;

  const [, month, day, year] = match.map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));

  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) return null;

  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

if (typeof document !== 'undefined') {
  let referralPanelState = null;

  initLoyaltyBirthdayForm();

  const setAttribute = (element, name, value) => {
    if (element && element.getAttribute(name) !== value) element.setAttribute(name, value);
  };

  const setText = (element, value) => {
    if (element && element.textContent !== value) element.textContent = value;
  };

  const update = () => {
    const root = document.querySelector('.lion-integrated-page:has(.sigma-loyalty-page-title)');
    if (!root) return;

    initLoyaltyNotifications();

    for (const [selector, id] of [
      ['.lion-redeem', 'sigma-rewards'],
      ['.lion-earn', 'sigma-earn'],
      ['.lion-account-history', 'sigma-history'],
    ]) {
      setAttribute(root.querySelector(selector), 'id', id);
    }

    const rulesList = root.querySelector('.lion-earn .lion-rules-list');
    const restedTemplate = document.querySelector('#sigma-rested-rule-card');
    if (rulesList && restedTemplate && !rulesList.querySelector(':scope > .sigma-rested-rule-card')) {
      rulesList.append(restedTemplate.content.cloneNode(true));
    }
    // Keep both purchase cards together in the visual and keyboard reading order.
    const purchaseRule = rulesList?.querySelector(':scope > .lion-rule-item--purchase:not(.sigma-rested-rule-card)');
    const restedRule = rulesList?.querySelector(':scope > .sigma-rested-rule-card');
    if (purchaseRule && restedRule && purchaseRule.nextElementSibling !== restedRule) {
      purchaseRule.after(restedRule);
    }

    for (const title of [
      rulesList?.querySelector('.lion-rule-item--birthday .lion-rule-item__title'),
      document.querySelector('.lion-modal--rule-action:has(.lion-birthday-action) .lion-modal__title'),
    ]) {
      if (title?.textContent.trim() === 'Happy Birthday!') {
        setText(title, 'Your birthday treat');
      }
    }

    const instagramAction = rulesList?.querySelector('.lion-instagram-follow__button');
    if (instagramAction?.textContent === 'Follow @sigmacoffeeuk') {
      setAttribute(instagramAction, 'aria-label', 'Follow @sigmacoffeeuk on Instagram');
      setText(instagramAction, 'Follow on Instagram');
    }

    root.querySelectorAll('.lion-integrated-page-section__heading-text').forEach((heading) => {
      setAttribute(heading, 'role', 'heading');
      setAttribute(heading, 'aria-level', '2');
    });

    root.querySelectorAll('.lion-faq-list__question-header').forEach((heading) => {
      setAttribute(heading, 'aria-level', '3');
    });

    const tier = root.querySelector('.sigma-loyalty-current-tier')?.textContent.trim();
    root.querySelectorAll('[data-sigma-tier]').forEach((tierCard) => {
      if (tier && tierCard.dataset.sigmaTier === tier) setAttribute(tierCard, 'aria-current', 'true');
      else tierCard.removeAttribute('aria-current');
    });

    root.querySelectorAll('.lion-history-table__row-cell:first-child').forEach((cell) => {
      const formatted = formatHistoryDate(cell.textContent);
      if (formatted) setText(cell, formatted);
    });

    const referral = document.querySelector('.lion-referral-widget-modal');
    if (referral) {
      const close = referral.querySelector('.lion-referral-widget-modal__close-button');
      setAttribute(close, 'aria-label', 'Close referral dialog');
      const closeTemplate = document.querySelector('#sigma-reward-close-content');
      if (close && closeTemplate && !close.classList.contains('site-close-button')) {
        close.classList.add('site-close-button');
        close.append(closeTemplate.content.cloneNode(true));
      }

      const heading = referral.querySelector('.lion-referral-widget-main__heading');
      setAttribute(heading, 'role', 'heading');
      setAttribute(heading, 'aria-level', '2');

      const shareLabels = [
        ['.lion-referral-share-button--twitter', 'Share referral on X'],
        ['.lion-referral-share-button--whatsapp', 'Share referral on WhatsApp'],
        ['.lion-referral-share-button--instagram', 'Share referral on Instagram'],
        ['.lion-referral-share-button--email', 'Share referral by email'],
      ];
      shareLabels.forEach(([selector, label]) => {
        setAttribute(referral.querySelector(selector), 'aria-label', label);
      });

      const linkContainer = referral.querySelector('.lion-referral-widget-main__share-link-container');
      setAttribute(linkContainer, 'role', 'group');
      setAttribute(linkContainer, 'aria-label', 'Referral link');

      const recipients = referral.querySelector('#referral-email-recipients, input[placeholder*="@"]');
      setAttribute(recipients, 'inputmode', 'email');
      setAttribute(recipients, 'autocapitalize', 'none');
      setAttribute(recipients, 'spellcheck', 'false');
      setAttribute(referral.querySelector('#referral-email-name'), 'autocomplete', 'name');

      const emailOpen = Boolean(referral.querySelector('.lion-referral-widget-panels--slide'));
      setAttribute(referral.querySelector('.lion-referral-share-button--email'), 'aria-expanded', String(emailOpen));
      if (referralPanelState?.element === referral && referralPanelState.emailOpen !== emailOpen) {
        const destination = emailOpen
          ? recipients
          : referral.querySelector('.lion-referral-share-button--email');

        requestAnimationFrame(() => {
          const panelStillMatches = Boolean(
            referral.querySelector('.lion-referral-widget-panels--slide'),
          ) === emailOpen;

          if (destination?.isConnected && panelStillMatches) {
            destination.focus({ preventScroll: true });
          }
        });
      }

      referralPanelState = { element: referral, emailOpen };
    }
  };

  document.addEventListener('click', (event) => {
    if (!document.querySelector('.sigma-loyalty-page-title')) return;

    const trigger = event.target.closest('[data-sigma-loyalty-jump]');
    if (!trigger) return;

    const target = document.querySelector('.lion-redeem');
    if (!target) return;

    event.preventDefault();
    event.stopPropagation();

    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({
      behavior: reduceMotion ? 'instant' : 'smooth',
      block: 'start',
    });

    const heading = target.querySelector('.lion-integrated-page-section__heading-text');
    setAttribute(heading, 'tabindex', '-1');
    heading?.focus({ preventScroll: true });
  }, true);

  let queued = false;
  const observer = new MutationObserver(() => {
    if (queued) return;
    queued = true;

    queueMicrotask(() => {
      queued = false;
      observer.disconnect();
      update();
      observe();
    });
  });
  const observe = () => observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['class'],
  });

  update();
  observe();
}
